import field from './loss-contour-data.js';
import homeTitle from '../data/home-title.json';
import { mastheadTitleScale } from './masthead-title.js';

// One cached terrain texture; dots and terrain share world coordinates so the
// optimizer stays attached while the camera slowly recedes through the plane.
export function mountLossContour(hero) {
  const stage = hero.querySelector('.loss-contour-stage');
  if (!stage) return null;
  const makeSurface = () => {
    const element = document.createElement('canvas');
    return {element, context: element.getContext('2d')};
  };
  const display = makeSurface(), scene = makeSurface(), title = makeSurface();
  if (!display.context || !scene.context || !title.context) return null;
  const canvas = display.element;
  canvas.className = 'loss-contour-canvas';
  canvas.setAttribute('aria-hidden', 'true');
  stage.append(canvas);
  const fields = [field];
  // SVG and canvas use the same shaped Lufga outlines. Text APIs differ in
  // kerning/letter-spacing support on Safari, causing a jump at the handoff.
  // Use the authored outline, not the built SVG's d attribute: Parcel folds
  // SVG transforms into that path, which would apply the title placement twice.
  const titlePath = new Path2D(homeTitle.path);
  const mod = (v, n) => ((v % n) + n) % n;
  const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));
  const smooth = t => t * t * (3 - 2 * t);
  const wrapX = 1400, wrapZ = 1200, focal = 800;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const dark = matchMedia('(prefers-color-scheme: dark)');
  const mobileMedia = matchMedia('(max-width: 700px)');
  const print = matchMedia('print');
  const abort = new AbortController(), signal = abort.signal;
  const v = {
    canvas, ctx: display.context, scene, title,
    design: {layers: [{field: 0, rise: 370, horizon: 64, vp: 790, offset: 820, alpha: 1, pace: 1, phase: 0}]},
    state: {speed: 10, cadence: 3.2, background: .74},
    visible: false, travel: 230, elapsed: 2.4, revealElapsed: 0,
    last: 0, nextDraw: 0, raf: 0, view: {pixelWidth: 0},
    paletteDirty: true, titleDirty: true, bands: [],
  };
  let disposed = false, suspended = false, printing = false, ready = false, resizeRaf = 0;
  // Cache a seamless tile once per field/resolution; frames only resample the tile.
  const caches=new Map();
  function getField(index,width){
    const key=index+':'+width;if(caches.has(key))return caches.get(key);
    const surface=makeSurface(),height=width,ratio=width/wrapX;
    surface.element.width=width;surface.element.height=height;surface.context.setTransform(ratio,0,0,height/wrapZ,0,0);
    surface.context.strokeStyle='#fff';surface.context.lineWidth=2.8;surface.context.lineJoin='round';surface.context.lineCap='round';
    surface.context.stroke(new Path2D(fields[index].contours));
    const result={...surface,width,height,ratio,patterns:new WeakMap()};caches.set(key,result);return result;
  }
  // Perspective-correct texture mapping keeps the contours smooth at every depth.
  const gpuCanvas=document.createElement('canvas');
  let gl=null,gpu=null,gpuAttempted=false,gpuIdle=0;
  function initGpu(){
    if(!gl)return;
    function shader(type,source){const s=gl.createShader(type);gl.shaderSource(s,source);gl.compileShader(s);if(!gl.getShaderParameter(s,gl.COMPILE_STATUS)){gl.deleteShader(s);throw Error('Contour shader could not compile');}return s;}
    const vertex=shader(gl.VERTEX_SHADER,`attribute vec2 a_position;uniform vec4 u_view;uniform vec4 u_plane;uniform float u_scroll;varying vec2 v_uv;varying float v_z;void main(){float x=a_position.x;float z=a_position.y;float w=800.0+z;float p=800.0/w;float sx=u_plane.x+(x-u_plane.y+.08*z)*p;float sy=u_plane.z+u_plane.w*p;gl_Position=vec4((2.0*(sx-u_view.x)/u_view.y-1.0)*w,(1.0-2.0*sy/u_view.z)*w,0.0,w);v_uv=vec2(x/1400.0,(z-u_scroll)/1200.0);v_z=z;}`);
    const fragment=shader(gl.FRAGMENT_SHADER,`precision mediump float;uniform sampler2D u_texture;uniform float u_alpha;varying vec2 v_uv;varying float v_z;void main(){float p=800.0/(800.0+v_z);float fade=smoothstep(.22,.40,p)*(1.0-smoothstep(.915,1.0,p));float a=smoothstep(.045,.45,texture2D(u_texture,v_uv).a)*u_alpha*fade;gl_FragColor=vec4(a,a,a,a);}`);
    const program=gl.createProgram();gl.attachShader(program,vertex);gl.attachShader(program,fragment);gl.linkProgram(program);gl.deleteShader(vertex);gl.deleteShader(fragment);if(!gl.getProgramParameter(program,gl.LINK_STATUS)){gl.deleteProgram(program);throw Error('Contour shader could not link');}
    const buffer=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,buffer);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-6000,0,6000,0,-6000,2836,-6000,2836,6000,0,6000,2836]),gl.STATIC_DRAW);
    gpu={program,buffer,extent:6000,position:gl.getAttribLocation(program,'a_position'),view:gl.getUniformLocation(program,'u_view'),plane:gl.getUniformLocation(program,'u_plane'),scroll:gl.getUniformLocation(program,'u_scroll'),alpha:gl.getUniformLocation(program,'u_alpha'),texture:gl.getUniformLocation(program,'u_texture'),textures:new Map()};
  }
  function gpuTexture(index){
    if(gpu.textures.has(index))return gpu.textures.get(index);
    const tile=getField(index,1024),texture=gl.createTexture();gl.bindTexture(gl.TEXTURE_2D,texture);gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL,true);gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,tile.element);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.REPEAT);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.REPEAT);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR_MIPMAP_LINEAR);gl.generateMipmap(gl.TEXTURE_2D);const aniso=gl.getExtension('EXT_texture_filter_anisotropic');if(aniso)gl.texParameterf(gl.TEXTURE_2D,aniso.TEXTURE_MAX_ANISOTROPY_EXT,Math.min(8,gl.getParameter(aniso.MAX_TEXTURE_MAX_ANISOTROPY_EXT)));gpu.textures.set(index,texture);return texture;
  }
  function drawTerrain(v){
    if(!gpu)return false;
    const{pixelWidth,pixelHeight,width,height,left}=v.view;
    if(gpuCanvas.width!==pixelWidth||gpuCanvas.height!==pixelHeight){gpuCanvas.width=pixelWidth;gpuCanvas.height=pixelHeight;}
    // Extend the same two triangles on panoramic screens; no extra mesh work.
    const extent=Math.max(6000,width*3);
    if(gpu.extent!==extent){
      gl.bindBuffer(gl.ARRAY_BUFFER,gpu.buffer);
      gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-extent,0,extent,0,-extent,2836,-extent,2836,extent,0,extent,2836]),gl.STATIC_DRAW);
      gpu.extent=extent;
    }
    gl.viewport(0,0,pixelWidth,pixelHeight);gl.clearColor(0,0,0,0);gl.clear(gl.COLOR_BUFFER_BIT);gl.useProgram(gpu.program);gl.bindBuffer(gl.ARRAY_BUFFER,gpu.buffer);gl.enableVertexAttribArray(gpu.position);gl.vertexAttribPointer(gpu.position,2,gl.FLOAT,false,0,0);gl.enable(gl.BLEND);gl.blendFunc(gl.ONE,gl.ONE_MINUS_SRC_ALPHA);gl.uniform4f(gpu.view,left,width,height,0);gl.activeTexture(gl.TEXTURE0);gl.uniform1i(gpu.texture,0);
    for(const layer of v.design.layers){gl.bindTexture(gl.TEXTURE_2D,gpuTexture(layer.field));gl.uniform4f(gpu.plane,layer.vp,layer.offset,layer.horizon,layer.rise);gl.uniform1f(gpu.scroll,mod(v.travel*layer.pace,wrapZ));gl.uniform1f(gpu.alpha,layer.alpha);gl.drawArrays(gl.TRIANGLES,0,6);}
    v.scene.context.drawImage(gpuCanvas,0,0);return true;
  }
  function queueGpu(){
    if(gpuAttempted||gpuIdle||disposed||!ready||document.hidden||print.matches||printing||!v.visible)return;
    const run=()=>{gpuIdle=0;if(disposed||document.hidden||print.matches||printing||!v.visible)return;gpuAttempted=true;gl=gpuCanvas.getContext('webgl',{alpha:true,antialias:false,premultipliedAlpha:true,depth:false,stencil:false,powerPreference:'low-power'});try{initGpu();}catch{gpu=null;}if(v.visible)render();};
    // The text and cached 2D terrain paint first; acquire the GPU only when visible.
    gpuIdle=globalThis.requestIdleCallback?requestIdleCallback(run,{timeout:1000}):setTimeout(run,150);
  }
  gpuCanvas.addEventListener('webglcontextlost',e=>{e.preventDefault();gpu=null;if(v.visible)render();},{signal});
  gpuCanvas.addEventListener('webglcontextrestored',()=>{if(disposed)return;try{initGpu();}catch{gpu=null;}if(v.visible)render();},{signal});

  function readPalette(v) {
    const css = getComputedStyle(hero);
    v.palette = { ink: css.color, wire: css.getPropertyValue('--loss-wire').trim(), teal: '#00e6c3' };
    v.paletteDirty = false;
    v.titleDirty = true;
  }
  function world(c, v) {
    c.setTransform(v.view.ratio, 0, 0, v.view.ratio, -v.view.left * v.view.ratio, 0);
  }
  function buildTitle(v) {
    const c = v.title.context;
    c.setTransform(1, 0, 0, 1, 0, 0);
    c.clearRect(0, 0, v.view.pixelWidth, v.view.pixelHeight);
    world(c, v);
    c.fillStyle = v.palette.ink;
    // Keep exactly the same uniform glyph scale and baseline as the SVG shell.
    const titleScale = v.view.titleScale;
    c.translate(v.view.mobile ? 210 : 600 - 550 * titleScale, 240 + 45 * titleScale);
    const scale = (v.view.mobile ? .7347119 : 1.0361322) * titleScale;
    c.scale(scale, scale);
    c.fill(titlePath);
    v.titleDirty = false;
  }
  function buildBands(v){
    v.bands=v.design.layers.map(layer=>{
      const result=[],first=Math.ceil((layer.horizon+layer.rise*.22)*v.view.ratio),last=Math.floor((layer.horizon+layer.rise)*v.view.ratio);
      for(let row=first;row<last;row++){
        const y0=row/v.view.ratio,y1=(row+1)/v.view.ratio,p0=(y0-layer.horizon)/layer.rise,p1=(y1-layer.horizon)/layer.rise,mid=(p0+p1)/2;
        const z0=focal*(1/p0-1),z1=focal*(1/p1-1),z=(z0+z1)/2,x=layer.vp+(-layer.offset+.08*z)*mid;
        const alpha=layer.alpha*smooth(clamp((mid-.22)/.18))*smooth(clamp((1-mid)/.085));
        result.push({x,y0,height:y1-y0,p:mid,z0,z1,alpha});
      }return result;
    });
  }
  function resize() {
    resizeRaf = 0;
    if (disposed) return;
    const {width: cssWidth, height: cssHeight} = stage.getBoundingClientRect();
    if (!cssWidth || !cssHeight) return;
    const mobile = mobileMedia.matches, height = mobile ? 470 : 480;
    // Once CSS caps the hero height, widen the world around its existing center.
    // The title and terrain retain their scale rather than stretching sideways.
    const width = cssWidth / cssHeight * height, left = 600 - width / 2;
    // Match the centered SVG shell; the landscape can keep widening independently.
    const titleScale = mastheadTitleScale(stage, cssHeight / height, mobile ? 840 : 1200);
    // Budget total pixels rather than width so panoramic screens stay sharp
    // without increasing the previous maximum canvas area.
    const dpr = Math.min(window.devicePixelRatio || 1, 2, Math.sqrt((1536 * 614) / (cssWidth * cssHeight)));
    const pixelWidth = Math.round(cssWidth * dpr), pixelHeight = Math.round(pixelWidth * height / width);
    if (pixelWidth !== v.view.pixelWidth || pixelHeight !== v.view.pixelHeight || mobile !== v.view.mobile) {
      v.view = {width, height, left, ratio: pixelWidth / width, pixelWidth, pixelHeight, mobile, titleScale};
      for (const canvas of [v.canvas, v.scene.element, v.title.element]) {
        canvas.width = pixelWidth;
        canvas.height = pixelHeight;
      }
      v.titleDirty = true;
      buildBands(v);
    }
    if (v.visible) render();
    schedule();
  }
  function queueResize() {
    if (!resizeRaf && !disposed) resizeRaf = requestAnimationFrame(resize);
  }
  function point(layer,x,z){const p=focal/(focal+z);return[layer.vp+(x-layer.offset+.08*z)*p,layer.horizon+layer.rise*p,p];}
  function depthAlpha(z){const p=focal/(focal+z);return smooth(clamp((p-.22)/.18))*smooth(clamp((1-p)/.085));}
  function sampleTrack(track,distance){
    const s=clamp(distance,0,track.length),lengths=track.cumulative;
    let lo=0,hi=lengths.length-1;while(lo+1<hi){const mid=(lo+hi)>>1;if(lengths[mid]<=s)lo=mid;else hi=mid;}
    const a=track.points[lo],b=track.points[lo+1],t=(s-lengths[lo])/(lengths[lo+1]-lengths[lo]);
    return[a[0]+(b[0]-a[0])*t,a[1]+(b[1]-a[1])*t];
  }
  function particleState(v,layer,serial,xcopy){
    const tracks=fields[layer.field].tracks,track=tracks[mod(serial+xcopy+layer.field,tracks.length)];
    const steps=track.stops.length-1,cadence=v.state.cadence*(layer.field===1?1.11:1),duration=steps*cadence,cycle=duration+4;
    const time=mod(v.elapsed+cycle*mod(layer.phase+serial*.31+xcopy*.19,1),cycle),settled=time>=duration;
    const index=Math.min(steps-1,Math.floor(time/cadence)),phase=settled?1:mod(time/cadence,1);
    const progress=settled?1:smooth(clamp((phase-.34)/.57));
    const distance=track.stops[index]+(track.stops[index+1]-track.stops[index])*progress;
    const fade=smooth(clamp(time/.8))*smooth(clamp((cycle-time)/1.1));
    return{track,time,settled,index,phase,distance,fade};
  }
  function drawParticle(c,v,layer,serial,xcopy){
    const {track,settled,index,phase,distance,fade}=particleState(v,layer,serial,xcopy);
    const surface=sampleTrack(track,distance),shift=serial*wrapZ+v.travel*layer.pace,z=surface[1]+shift;
    if(z<0||z>focal*(1/.22-1))return;
    const alpha=layer.alpha*depthAlpha(z)*fade;if(alpha<.025)return;
    const p=point(layer,surface[0]+xcopy*wrapX,z);
    if(p[0]<v.view.left-110||p[0]>v.view.left+v.view.width+110)return;
    const projectAt=s=>{const q=sampleTrack(track,s);return point(layer,q[0]+xcopy*wrapX,q[1]+shift);};
    // The short wake follows the precomputed negative-gradient trajectory.
    c.strokeStyle='#fff';c.fillStyle='#fff';c.lineCap='round';c.lineJoin='round';
    const wake=Math.min(145,distance),wakeSegments=12;
    let previous=projectAt(distance-wake);
    for(let i=1;i<=wakeSegments;i++){
      const q=projectAt(distance-wake*(1-i/wakeSegments));
      c.globalAlpha=alpha*.8*(i/wakeSegments)**1.6;c.lineWidth=3.1;c.beginPath();c.moveTo(previous[0],previous[1]);c.lineTo(q[0],q[1]);c.stroke();previous=q;
    }
    // Preview only the next downhill step; it naturally shrinks near the basin.
    const arrowAlpha=settled?0:clamp((phase-.12)/.14)*clamp((.80-phase)/.18);
    const ahead=track.stops[index+1]-distance,end=projectAt(track.stops[index+1]);
    const screenReach=Math.hypot(end[0]-p[0],end[1]-p[1]);
    if(arrowAlpha>0&&screenReach>12&&ahead>0){
      const samples=10,gap=Math.min(ahead*.25,13/Math.max(.25,p[2]));let last=p,tip=p;
      c.globalAlpha=alpha*arrowAlpha;c.lineWidth=2.5;c.beginPath();
      for(let i=0;i<=samples;i++){const q=projectAt(distance+gap+(ahead-gap)*i/samples);if(i===0)c.moveTo(q[0],q[1]);else c.lineTo(q[0],q[1]);last=tip;tip=q;}
      c.stroke();const d=Math.max(.001,Math.hypot(tip[0]-last[0],tip[1]-last[1])),vx=(tip[0]-last[0])/d,vy=(tip[1]-last[1])/d,head=Math.min(8,screenReach*.25);
      c.beginPath();c.moveTo(tip[0]-head*vx+head*.5*vy,tip[1]-head*vy-head*.5*vx);c.lineTo(tip[0],tip[1]);c.lineTo(tip[0]-head*vx-head*.5*vy,tip[1]-head*vy+head*.5*vx);c.stroke();
    }
    const pulse=clamp(phase/.22),pulsing=!settled&&phase<.22,radius=5.5+3*p[2]+(pulsing?Math.sin(pulse*Math.PI)*1.6:0);
    c.globalAlpha=alpha*(pulsing?(1-pulse)*.7:settled?.3:.07);c.lineWidth=1.4;c.beginPath();c.arc(p[0],p[1],pulsing?10+18*pulse:13,0,Math.PI*2);c.stroke();
    c.globalCompositeOperation='destination-out';c.globalAlpha=alpha;c.lineWidth=2.2;c.beginPath();c.arc(p[0],p[1],radius,0,Math.PI*2);c.stroke();
    c.globalCompositeOperation='source-over';c.globalAlpha=alpha;c.beginPath();c.arc(p[0],p[1],radius,0,Math.PI*2);c.fill();
  }
  function draw(v){
    if(disposed||!v.view.pixelWidth)return;if(v.paletteDirty)readPalette(v);if(v.titleDirty)buildTitle(v);
    const c=v.scene.context,{pixelWidth,pixelHeight,ratio,left,width}=v.view;
    const dx=0,dy=0;
    if(reduced.matches)v.revealElapsed=.8;
    const reveal=smooth(clamp(v.revealElapsed/.8));
    c.setTransform(1,0,0,1,0,0);c.globalCompositeOperation='source-over';c.globalAlpha=1;c.clearRect(0,0,pixelWidth,pixelHeight);
    const gpuDrawn=drawTerrain(v);queueGpu();
    v.design.layers.forEach((layer,li)=>{
      if(!gpuDrawn){
      const tile=getField(layer.field,1024);let pattern=tile.patterns.get(c);if(!pattern){pattern=c.createPattern(tile.element,'repeat');tile.patterns.set(c,pattern);}
      c.fillStyle=pattern;
      for(const b of v.bands[li]){
        const scroll=mod(v.travel*layer.pace,wrapZ),sx=wrapX/tile.width*b.p,sy=b.height/((b.z1-b.z0)/wrapZ*tile.height),sourceY=(b.z0-scroll)/wrapZ*tile.height;
        c.setTransform(ratio*sx,0,0,ratio*sy,(b.x-left)*ratio,(b.y0-sourceY*sy)*ratio);c.globalAlpha=b.alpha;
        c.fillRect((left-b.x)/sx,sourceY,width/sx,(b.z1-b.z0)/wrapZ*tile.height);
      }
      }
      world(c,v);const lap=Math.floor(v.travel*layer.pace/wrapZ);
      for(let copy=-2;copy<=3;copy++)for(let xcopy=-2;xcopy<=2;xcopy++)drawParticle(c,v,layer,copy-lap,xcopy);
    });
    c.setTransform(1,0,0,1,0,0);c.globalAlpha=1;c.globalCompositeOperation='source-in';c.fillStyle=v.palette.wire;c.fillRect(0,0,pixelWidth,pixelHeight);
    v.ctx.setTransform(1,0,0,1,0,0);v.ctx.clearRect(0,0,pixelWidth,pixelHeight);v.ctx.globalAlpha=v.state.background*reveal;v.ctx.drawImage(v.scene.element,dx,dy);
    // Cut background wires out of the letters, leaving the persistent SVG ink
    // below untouched. Only the teal overlap is composited over that title.
    v.ctx.globalAlpha=1;v.ctx.globalCompositeOperation='destination-out';v.ctx.drawImage(v.title.element,0,0);v.ctx.globalCompositeOperation='source-over';
    c.fillStyle=v.palette.teal;c.fillRect(0,0,pixelWidth,pixelHeight);c.globalCompositeOperation='destination-in';c.drawImage(v.title.element,-dx,-dy);v.ctx.globalAlpha=reveal;v.ctx.drawImage(v.scene.element,dx,dy);v.ctx.globalAlpha=1;
  }
  // A failed canvas enhancement leaves the outlined SVG shell in place.
  function render() {
    if (disposed || !ready || !v.view.pixelWidth || print.matches || printing) return;
    try {
      draw(v);
      stage.classList.add('loss-contour-ready');
    } catch {
      dispose();
    }
  }
  function runnable() {
    return ready && !reduced.matches && v.visible && !document.hidden && !print.matches && !printing && !suspended && !disposed;
  }
  function frame(now) {
    v.raf = 0;
    if (!hero.isConnected) { dispose(); return; }
    if (!runnable()) return;
    const dt = v.last ? Math.min((now - v.last) / 1000, .1) : 0;
    v.last = now;
    v.travel += dt * v.state.speed;
    v.elapsed += dt;
    v.revealElapsed = Math.min(.8, v.revealElapsed+dt);
    if (now + .5 >= v.nextDraw) {
      render();
      const interval = 1000 / (v.view.mobile ? 24 : 30);
      v.nextDraw += interval;
      if (v.nextDraw < now - interval) v.nextDraw = now + interval;
    }
    if (runnable()) v.raf = requestAnimationFrame(frame);
  }
  function schedule() {
    cancelAnimationFrame(v.raf);
    v.raf = 0;
    v.last = 0;
    v.nextDraw = performance.now();
    if (runnable()) v.raf = requestAnimationFrame(frame);
  }
  const resizeObserver = new ResizeObserver(queueResize);
  resizeObserver.observe(stage);
  const intersection = new IntersectionObserver(([entry]) => {
    if (!hero.isConnected) { dispose(); return; }
    v.visible = entry.isIntersecting;
    if (v.visible) resize();
    else schedule();
  }, {threshold: .01});
  intersection.observe(stage);
  function themeChanged() {
    v.paletteDirty = true;
    if (v.visible) render();
  }
  const themeObserver = new MutationObserver(themeChanged);
  for (const node of [document.documentElement, document.body]) {
    themeObserver.observe(node, {attributes: true, attributeFilter: ['class', 'style', 'data-theme', 'data-color-mode']});
  }
  dark.addEventListener('change', themeChanged, {signal});
  reduced.addEventListener('change', () => {
    if (v.visible) render();
    schedule();
  }, {signal});
  mobileMedia.addEventListener('change', queueResize, {signal});
  print.addEventListener('change', () => { if (!print.matches) queueResize(); schedule(); }, {signal});
  document.addEventListener('visibilitychange', () => { if (!document.hidden) queueResize(); schedule(); }, {signal});
  window.addEventListener('resize', queueResize, {signal});
  window.addEventListener('beforeprint', () => { printing = true; schedule(); }, {signal});
  window.addEventListener('afterprint', () => { printing = false; queueResize(); schedule(); }, {signal});
  window.addEventListener('pagehide', event => {
    if (!event.persisted) { dispose(); return; }
    suspended = true;
    schedule();
  }, {signal});
  window.addEventListener('pageshow', () => { suspended = false; queueResize(); schedule(); }, {signal});
  let dprMedia;
  function watchDpr() {
    dprMedia?.removeEventListener('change', dprChanged);
    dprMedia = matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
    dprMedia.addEventListener('change', dprChanged, {once: true});
  }
  function dprChanged() { watchDpr(); queueResize(); }
  function dispose() {
    if (disposed) return;
    disposed = true;
    abort.abort();
    cancelAnimationFrame(v.raf);
    cancelAnimationFrame(resizeRaf);
    if (gpuIdle) {
      if (globalThis.cancelIdleCallback) cancelIdleCallback(gpuIdle);
      else clearTimeout(gpuIdle);
    }
    resizeObserver.disconnect();
    intersection.disconnect();
    themeObserver.disconnect();
    dprMedia?.removeEventListener('change', dprChanged);
    for (const surface of [scene, title]) surface.element.width = surface.element.height = 1;
    for (const cache of caches.values()) cache.element.width = cache.element.height = 1;
    caches.clear();
    if (gpu) {
      for (const texture of gpu.textures.values()) gl.deleteTexture(texture);
      gl.deleteBuffer(gpu.buffer);
      gl.deleteProgram(gpu.program);
      gpu = null;
    }
    gpuCanvas.width = gpuCanvas.height = 1;
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
    canvas.remove();
    stage.classList.remove('loss-contour-ready');
  }
  watchDpr();
  resize();
  // Outlines need no font download; reveal only after the first complete draw.
  Promise.resolve().then(() => {
    if (disposed) return;
    ready = true;
    v.titleDirty = true;
    resize();
  }).catch(dispose);
  return {dispose};
}
